import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignIn = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError(err?.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (demoEmail) => {
    setEmail(demoEmail);
    setPassword('password123');
  };

  return (
    <>
      <Helmet>
        <title>Sign In - Mosey</title>
        <meta name="description" content="Sign in to access your meeting confirmations and manage participants." />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-card border border-border rounded-lg shadow-md p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-primary" />
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
                Welcome Back
              </h1>
              <p className="text-sm text-muted-foreground">
                Sign in to manage your meeting confirmations
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
                <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="your.email@example.com"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                iconName={loading ? 'Loader2' : 'LogIn'}
                iconPosition="left"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3 text-center">
                Demo Accounts (Password: password123)
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleDemoLogin('sarah.johnson@example.com')}
                  disabled={loading}
                  className="justify-start text-sm"
                >
                  <Icon name="User" size={16} className="mr-2" />
                  Sarah Johnson (Admin)
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleDemoLogin('michael.chen@example.com')}
                  disabled={loading}
                  className="justify-start text-sm"
                >
                  <Icon name="User" size={16} className="mr-2" />
                  Michael Chen
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleDemoLogin('emily.rodriguez@example.com')}
                  disabled={loading}
                  className="justify-start text-sm"
                >
                  <Icon name="User" size={16} className="mr-2" />
                  Emily Rodriguez
                </Button>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="text-sm text-primary hover:underline"
                disabled={loading}
              >
                Don't have an account? Sign up
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;
